import React from 'react';
import PropTypes from 'prop-types';
import { 
  Box, 
  Typography, 
  Grid,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  InputAdornment,
  Chip,
  Tooltip,
  Divider
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import EventIcon from '@mui/icons-material/Event';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import { motion } from 'framer-motion';

const AppointmentsFilter = ({ 
  searchTerm, 
  filterCalendar, 
  filterEmployee,
  filterStatus, 
  availableCalendars,
  availableEmployees,
  onSearchChange,
  onCalendarChange,
  onEmployeeChange,
  onStatusChange,
  onClearFilters
}) => {
  // Count active filters
  const activeFilterCount = [
    searchTerm, 
    filterCalendar !== 'all', 
    filterEmployee !== 'all',
    filterStatus !== 'all'
  ].filter(Boolean).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      <Box 
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.subtle'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography 
            variant="h6" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontWeight: 'bold'
            }}
          >
            <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
            Filter Appointments
            {activeFilterCount > 0 && (
              <Chip 
                label={activeFilterCount} 
                size="small" 
                color="primary" 
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          
          {activeFilterCount > 0 && (
            <Button 
              variant="outlined" 
              size="small"
              onClick={onClearFilters}
              startIcon={<FilterListIcon />}
            >
              Clear All Filters
            </Button>
          )}
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Search"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search by name, calendar..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Calendar</InputLabel>
              <Select
                value={filterCalendar}
                onChange={(e) => onCalendarChange(e.target.value)}
                label="Calendar"
                startAdornment={
                  <InputAdornment position="start">
                    <EventIcon color="action" />
                  </InputAdornment>
                }
                sx={{
                  borderRadius: 2
                }}
              >
                <MenuItem value="all">All Calendars</MenuItem>
                {availableCalendars.map((calendar) => (
                  <MenuItem key={calendar} value={calendar}>
                    {calendar}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select
                value={filterEmployee}
                onChange={(e) => onEmployeeChange(e.target.value)}
                label="Employee"
                startAdornment={
                  <InputAdornment position="start">
                    <PersonIcon color="action" />
                  </InputAdornment>
                }
                sx={{
                  borderRadius: 2
                }}
              >
                <MenuItem value="all">All Employees</MenuItem>
                {availableEmployees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => onStatusChange(e.target.value)}
                label="Status"
                startAdornment={
                  <InputAdornment position="start">
                    <AssignmentIcon color="action" />
                  </InputAdornment>
                }
                sx={{
                  borderRadius: 2
                }}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="rescheduled">Rescheduled</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        
        {activeFilterCount > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1 
            }}>
              {searchTerm && (
                <Tooltip title="Clear search term">
                  <Chip 
                    label={`Search: ${searchTerm}`} 
                    onDelete={() => onSearchChange('')}
                    color="primary"
                    variant="outlined"
                  />
                </Tooltip>
              )}
              
              {filterCalendar !== 'all' && (
                <Tooltip title="Clear calendar filter">
                  <Chip 
                    icon={<EventIcon />}
                    label={`Calendar: ${filterCalendar}`} 
                    onDelete={() => onCalendarChange('all')}
                    color="primary"
                    variant="outlined"
                  />
                </Tooltip>
              )}

              {filterEmployee !== 'all' && (
                <Tooltip title="Clear employee filter">
                  <Chip
                    icon={<PersonIcon />}
                    label={`Employee: ${availableEmployees.find((employee) => employee.id === filterEmployee)?.name || filterEmployee}`}
                    onDelete={() => onEmployeeChange('all')}
                    color="primary"
                    variant="outlined"
                  />
                </Tooltip>
              )}
              
              {filterStatus !== 'all' && (
                <Tooltip title="Clear status filter">
                  <Chip 
                    icon={<AssignmentIcon />}
                    label={`Status: ${filterStatus}`} 
                    onDelete={() => onStatusChange('all')}
                    color="primary"
                    variant="outlined"
                  />
                </Tooltip>
              )}
            </Box>
          </>
        )}
      </Box>
    </motion.div>
  );
};

AppointmentsFilter.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  filterCalendar: PropTypes.string.isRequired,
  filterEmployee: PropTypes.string.isRequired,
  filterStatus: PropTypes.string.isRequired,
  availableCalendars: PropTypes.array.isRequired,
  availableEmployees: PropTypes.array.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onCalendarChange: PropTypes.func.isRequired,
  onEmployeeChange: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired
};

export default AppointmentsFilter; 